import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { formatCurrency } from '../../../app/utils/formatters';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
  Grid,
  IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import api from '../../../app/api/api';
import { OrderStatus } from '../../../app/data/models/orders/orderStatus';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { Order } from '../../../app/data/models/orders/order';
import { getOrderTypeChipProps } from '../../../app/utils/orderUtils';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';

interface OrderListProps {
  restaurantId?: string;
  onOrderSelect: (order: Order) => void;
  preloadedOrders?: Order[];
}

interface PageData {
  orders: Order[];
  nextPageId: string | null;
}

const OrderList: React.FC<OrderListProps> = ({
  restaurantId,
  onOrderSelect,
  preloadedOrders,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Refs for maintaining state across renders
  const isInitialMount = useRef(true);
  const isUserNavigating = useRef(false);
  const pagesCache = useRef<Map<string, PageData>>(new Map());
  const activePageId = useRef<string>('0');
  const pageHistory = useRef<string[]>(['0']);
  const previousPreloadedOrdersRef = useRef<Order[] | undefined>(undefined);

  // State for UI rendering
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextPageId, setNextPageId] = useState<string | null>(null);
  const [searchQuery] = useState('');
  const [orderTypeFilter] = useState<string>('all');

  // Check if preloadedOrders have changed in a meaningful way (excluding minor updates)
  const haveOrdersSignificantlyChanged = useMemo(() => {
    if (!previousPreloadedOrdersRef.current || !preloadedOrders) return false;

    // If lengths are different, that's a significant change
    if (previousPreloadedOrdersRef.current.length !== preloadedOrders.length)
      return true;

    // Check if any orders are completely new (not just updated fields)
    const previousIds = new Set(
      previousPreloadedOrdersRef.current.map((o) => o.id)
    );
    const currentIds = new Set(preloadedOrders.map((o) => o.id));

    // If there are new IDs or missing IDs, that's significant
    for (const id of previousIds) {
      if (!currentIds.has(id)) return true;
    }

    for (const id of currentIds) {
      if (!previousIds.has(id)) return true;
    }

    return false;
  }, [preloadedOrders, previousPreloadedOrdersRef.current]);

  // Load initial data or use preloaded data
  useEffect(() => {
    // Always update the reference to the latest preloadedOrders
    if (preloadedOrders) {
      // Check if we should reset to the first page because of significant data changes
      const shouldResetToFirstPage =
        haveOrdersSignificantlyChanged && !isUserNavigating.current;

      // If we're on page 0 or should reset, or this is initial mount, use preloaded orders
      if (
        isInitialMount.current ||
        shouldResetToFirstPage ||
        activePageId.current === 0
      ) {
        setOrders(preloadedOrders);

        // If we have enough preloaded orders, set up for pagination
        if (preloadedOrders.length >= 10) {
          const lastOrderId = preloadedOrders[preloadedOrders.length - 1].id;
          setNextPageId(lastOrderId);

          // Cache the preloaded orders for page 0
          pagesCache.current.set('0', {
            orders: preloadedOrders,
            nextPageId: lastOrderId,
          });
        } else {
          setNextPageId(null);
        }

        // Reset navigation state if resetting to first page
        if (shouldResetToFirstPage) {
          activePageId.current = '0';
          pageHistory.current = ['0'];
        }
      } else {
        // We're on a different page, update the page 0 cache without changing the view
        if (preloadedOrders.length >= 10) {
          const lastOrderId = preloadedOrders[preloadedOrders.length - 1].id;

          // Only update the cache for page 0, but don't change the current view
          pagesCache.current.set('0', {
            orders: preloadedOrders,
            nextPageId: lastOrderId,
          });
        }
      }

      // Store the current preloadedOrders for future comparison
      previousPreloadedOrdersRef.current = preloadedOrders;
    } else if (restaurantId && !isUserNavigating.current) {
      // Only load from API if we're not currently navigating
      loadOrders();
    }

    // Reset the navigation flag after the effect runs
    isUserNavigating.current = false;

    // No longer initial mount after first run
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [restaurantId, preloadedOrders, haveOrdersSignificantlyChanged]);

  // Function to load orders with pagination
  const loadOrders = async (pageId?: string) => {
    if (!restaurantId) return;

    const targetPageId = pageId !== undefined ? pageId : 0;

    // Check if we have the page in cache
    if (pagesCache.current.has(targetPageId)) {
      const cachedData = pagesCache.current.get(targetPageId)!;
      setOrders(cachedData.orders);
      setNextPageId(cachedData.nextPageId);
      activePageId.current = targetPageId;
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.Order.list(restaurantId, pageId);

      if (!response || !response.data) {
        setError('Failed to load orders');
        setOrders([]);
        return;
      }

      // Filter orders
      const filteredOrders = response.data.filter(
        (order: Order) =>
          order.orderStatus === OrderStatus.Ready ||
          order.orderStatus === OrderStatus.Processing ||
          order.orderStatus === OrderStatus.Pending
      );

      // Process nextPageId
      const nextId =
        response.nextPageId !== null && response.nextPageId !== undefined
          ? response.nextPageId
          : null;

      // Store in cache
      pagesCache.current.set(targetPageId, {
        orders: filteredOrders,
        nextPageId: nextId,
      });

      // Update state
      setOrders(filteredOrders);
      setNextPageId(nextId);
      activePageId.current = targetPageId;
    } catch (error) {
      setError('Failed to load orders');
      setOrders([]);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle next page navigation
  const handleNextPage = () => {
    if (nextPageId === null) return;

    // Set flag that user is navigating
    isUserNavigating.current = true;

    // Update page history
    pageHistory.current = [...pageHistory.current, nextPageId];

    // Load the next page
    loadOrders(nextPageId);
  };

  // Handle previous page navigation
  const handlePreviousPage = () => {
    if (pageHistory.current.length <= 1) return;

    // Set flag that user is navigating
    isUserNavigating.current = true;

    // Update page history
    pageHistory.current.pop(); // Remove current page
    const previousPageId = pageHistory.current[pageHistory.current.length - 1];

    // Load the previous page
    loadOrders(previousPageId);
  };

  // Handle order selection while preserving pagination state
  const handleOrderSelect = (order: Order) => {
    // Call the parent's onOrderSelect handler
    onOrderSelect(order);
  };

  // Filter orders by search and type
  const filteredOrders = orders.filter((order) => {
    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        (order.tokenNumber && `${order.tokenNumber}`.includes(searchLower)) ||
        (order.customerName &&
          order.customerName.toLowerCase().includes(searchLower)) ||
        (order.waiterName &&
          order.waiterName.toLowerCase().includes(searchLower)) ||
        (order.tableNumber &&
          String(order.tableNumber).includes(searchQuery)) ||
        String(order.subtotal).includes(searchQuery) ||
        String(order.tokenNumber).includes(searchQuery) ||
        t(`order-type.${OrderType[order.orderType]?.toLowerCase()}`)
          .toLowerCase()
          .includes(searchLower) ||
        OrderType[order.orderType]?.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Filter by order type if not set to 'all'
    if (orderTypeFilter !== 'all') {
      const orderTypeValue = parseInt(orderTypeFilter);
      if (order.orderType !== orderTypeValue) return false;
    }

    return true;
  });

  // Determine if pagination controls should be shown
  const showPagination = nextPageId !== null || pageHistory.current.length > 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Helper function to get payment status
  const getPaymentStatus = (order: Order) => {
    const isPaid = order.isPaid;
    return {
      text: isPaid ? 'Paid' : 'Pending Payment',
      icon: isPaid ? (
        <PaidIcon fontSize="small" />
      ) : (
        <PendingIcon fontSize="small" />
      ),
      color: isPaid ? ('success' as const) : ('warning' as const),
    };
  };

  return (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {t('common.error.failed-to-load')}
        </Typography>
      )}

      {filteredOrders.length > 0 ? (
        <>
          <List sx={{ overflow: 'auto' }}>
            {filteredOrders.map((order) => {
              const paymentStatus = getPaymentStatus(order);
              const orderTypeProps = getOrderTypeChipProps(order.orderType);
              return (
                <ListItem
                  key={order.id}
                  disablePadding
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <ListItemButton onClick={() => handleOrderSelect(order)}>
                    <ListItemText
                      primary={
                        <Box sx={{ mt: 1 }}>
                          <Grid
                            container
                            spacing={1}
                            alignItems={isMobile ? 'flex-start' : 'center'}
                          >
                            <Grid item xs={12} sm={8}>
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}
                              >
                                #{order.tokenNumber}
                              </Typography>
                              {/* Table number for Dine-In, Customer name for Take-Away */}
                              {order.orderType === OrderType.DineIn && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5,
                                    mb: 0.5,
                                  }}
                                >
                                  <RestaurantIcon
                                    sx={{
                                      fontSize: 18,
                                      color: 'success.main',
                                      mr: 0.5,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="success.main"
                                    fontWeight="bold"
                                    sx={{
                                      fontSize: isMobile
                                        ? '0.92rem'
                                        : '1.02rem',
                                      letterSpacing: 0.2,
                                    }}
                                  >
                                    {t('biller.table')}{' '}
                                    {order.tableNumber
                                      ? order.tableNumber
                                      : t('biller.no-table')}
                                  </Typography>
                                </Box>
                              )}
                              {order.orderType === OrderType.TakeAway &&
                                order.customerName && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mt: 0.5,
                                      mb: 0.5,
                                    }}
                                  >
                                    <TakeoutDiningIcon
                                      sx={{
                                        fontSize: 18,
                                        color: 'warning.main',
                                        mr: 0.5,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="warning.main"
                                      fontWeight="bold"
                                      sx={{
                                        fontSize: isMobile
                                          ? '0.92rem'
                                          : '1.02rem',
                                        letterSpacing: 0.2,
                                      }}
                                    >
                                      {order.customerName}
                                    </Typography>
                                  </Box>
                                )}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: isMobile ? '0.85rem' : 'inherit',
                                }}
                              >
                                {order.orderDate &&
                                  format(new Date(order.orderDate), 'PPpp')}
                              </Typography>
                              <Box
                                sx={{
                                  mt: 1,
                                  display: 'flex',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Chip
                                  icon={paymentStatus.icon}
                                  label={t(
                                    `biller.${paymentStatus.text.toLowerCase().replace(' ', '-')}`
                                  )}
                                  size="small"
                                  color={paymentStatus.color}
                                  variant="outlined"
                                  sx={{
                                    borderRadius: '16px',
                                    fontSize: isMobile ? '0.8rem' : 'inherit',
                                  }}
                                />
                                <Chip
                                  {...orderTypeProps}
                                  label={t(orderTypeProps.label)}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    borderRadius: '16px',
                                    fontSize: isMobile ? '0.8rem' : 'inherit',
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: {
                                    xs: 'flex-start',
                                    sm: 'flex-end',
                                  },
                                  alignItems: 'center',
                                  height: '100%',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  component="span"
                                  fontWeight="bold"
                                  sx={{
                                    fontSize: isMobile ? '0.95rem' : '1.05rem',
                                  }}
                                >
                                  {t('biller.total')}:{' '}
                                  {formatCurrency(
                                    order.total || order.subtotal || 0
                                  )}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {showPagination && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                p: 1,
              }}
            >
              <IconButton
                onClick={handlePreviousPage}
                disabled={pageHistory.current.length <= 1}
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: (theme) =>
                      theme.palette.action.disabledBackground,
                  },
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                }}
              >
                <ChevronLeftIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
              <IconButton
                onClick={handleNextPage}
                disabled={nextPageId === null}
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: (theme) =>
                      theme.palette.action.disabledBackground,
                  },
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                }}
              >
                <ChevronRightIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('biller.no-orders-found')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OrderList;
